import { useEffect, useState, type ReactNode } from "react";
import {
  CameraIcon,
  CameraOffIcon,
  CheckIcon,
  CheckboxRow,
  DashButton,
  DataSurface,
  InfoIcon,
  PrivacyIcon,
  StatusPill,
  type SurfaceState,
} from "../../shared/components";
import { getSchoolSettings, saveSchoolSettings } from "../../shared/api/school";
import { getStaffSession } from "../../shared/stores/staffSession";
import type { Lang } from "../../shared/i18n";
import {
  getReduceMotionSetting,
  setReduceMotionSetting,
} from "../../shared/hooks/useReduceMotion";
import {
  getPlayMode,
  setPlayMode,
  PLAY_MODES,
  type PlayMode,
} from "../../shared/stores/playPreference";
import { SETTINGS_COPY } from "./settingsCopy";
import {
  describeCameraState,
  settingsTabsFor,
  type CameraState,
  type SettingsTabKey,
} from "./settingsModel";
import type { RouteId } from "../../app/router/routes";
import type { Role } from "../../shared/types/prisma-enums";

/**
 * G1–G4 — every signed-in adult's own settings.
 *
 * 🔴 FOUR SCREENS `information-architecture.md` §1 ROUTES AND NOTHING BUILT.
 * Two of them were worse than missing: `useReduceMotion.ts` applies an in-app
 * switch it says is *"surfaced on G3 Accessibility (Unit 10)"* — so the setting
 * was honoured everywhere and settable nowhere — and until `POST /auth/password`
 * shipped with this work, **nothing in the product could rotate a staff
 * credential**.
 *
 * 🔴 THEY ARE NOT A TIER. A teacher, a school admin and a platform operator all
 * change their own password and their own language, so these carry no `roles`
 * and appear in no sidebar; the shell offers one Settings control beside
 * sign-out. §2's per-tier navigation budgets are unchanged.
 *
 * ⚠️ NOTHING HERE IS A CHILD-FACING SETTING. `settings-and-controls.md` §7 lists
 * *"child-facing settings of any kind"* on the Never list — four-year-olds do
 * not have a settings screen. The play-mode control on G3 is the ADULT's copy of
 * a choice the child is offered in their own flow, in words a child never reads.
 */

const cardStyle = { border: "1px solid var(--color-line)" } as const;

export interface SettingsProps {
  lang?: Lang;
  /** The access token, for G1's write. Absent for a signed-out preview. */
  token?: string;
  /**
   * 🔴 WHICH TABS THIS PERSON HAS — see `settingsTabsFor`. Absent means the full
   * classroom set, which is what a signed-out preview and every tier but the
   * school's should see.
   */
  role?: Role | null;
  navigate?: (id: RouteId) => void;
}

/* ─────────────────────────────────────────────────────────── shell ── */

/**
 * The settings screens share a heading and a sub-nav; each renders its own body.
 *
 * ⚠️ STILL EXPORTED, THOUGH NO OTHER FILE DRAWS INTO IT ANY MORE. The school
 * settings screen used to render its own body inside this frame, which is what
 * made E6 and an admin's account two tabs of one screen; both have since been
 * removed. What is left are the two classroom screens in this file, and the
 * export stays because the frame is the shared contract a future tab renders
 * into — not because something outside currently uses it.
 *
 * ⚠️ THE TAB LIST IS NOT A CONSTANT. It was four hard-coded rows, which is how
 * an admin came to be offered a camera-permission panel for a webcam no child
 * sits in front of. `settingsTabsFor` owns that decision and is tested — and it
 * now returns `[]` for the whole school tier, so this frame draws no strip at
 * all for them.
 */
export function SettingsFrame({
  lang,
  active,
  role,
  navigate,
  children,
}: {
  lang: Lang;
  active: RouteId;
  role?: Role | null;
  navigate?: (id: RouteId) => void;
  children: ReactNode;
}) {
  const t = SETTINGS_COPY[lang];
  const LABEL: Record<SettingsTabKey, string> = {
    accessibility: t.tabAccessibility,
    camera: t.tabCamera,
  };
  const tabs = settingsTabsFor(role ?? null).map((tab) => ({
    id: tab.id,
    label: LABEL[tab.key],
  }));

  return (
    <section className="font-dash text-dash-sm text-ink">
      <h1 className="text-dash-lg font-bold">{t.title}</h1>

      <nav className="mt-4 flex flex-wrap gap-2" aria-label={t.title}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            aria-current={tab.id === active ? "page" : undefined}
            onClick={() => navigate?.(tab.id)}
            className="rounded-pill px-4 py-2 font-dash text-dash-sm font-bold"
            style={
              tab.id === active
                ? {
                    background: "var(--color-primary)",
                    color: "var(--color-primary-ink)",
                  }
                : {
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-line)",
                  }
            }
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">{children}</div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── G3 ── */

/**
 * G3 — accessibility.
 *
 * 🔴 THE SWITCH THIS SCREEN EXISTS FOR HAS BEEN APPLIED SINCE UNIT 1 AND
 * SETTABLE NOWHERE. `useReduceMotion.ts` resolves the OS preference and an
 * in-app setting, stamps the root, and names this screen as where the second one
 * would be surfaced — so `kalqy.reduceMotion` could only ever be `false` unless
 * somebody wrote it by hand in a console.
 */
export function AccessibilitySettingsScreen({
  lang = "en",
  role,
  navigate,
}: SettingsProps) {
  const t = SETTINGS_COPY[lang];
  const [reduceMotion, setReduce] = useState(getReduceMotionSetting);
  /*
   * ⚠️ `getPlayMode` RETURNS `null` FOR "NOT CHOSEN YET", AND THAT IS NOT THE
   * SAME AS `tap`. Defaulting a never-asked child to tapping would quietly
   * decide the thing `playPreference.ts` exists to ask about — so nothing is
   * shown as selected until somebody picks.
   */
  const [mode, setMode] = useState<PlayMode | null>(getPlayMode);

  return (
    <SettingsFrame
      lang={lang}
      active="settings-accessibility"
      role={role}
      navigate={navigate}
    >
      <div className="max-w-xl rounded-xl bg-surface p-4" style={cardStyle}>
        <h2 className="text-dash-md font-bold">{t.motionTitle}</h2>

        <label className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={reduceMotion}
            onChange={(e) => {
              setReduce(e.target.checked);
              setReduceMotionSetting(e.target.checked);
            }}
          />
          <span className="max-w-prose">
            <span className="font-bold">{t.motionToggle}</span>
            {/* Not a preference: vestibular sensitivity is real, and some
                children find motion distressing (design-system.md §1). */}
            <span className="mt-1 block text-dash-xs text-muted">
              {t.motionWhy}
            </span>
          </span>
        </label>

        <p className="mt-3 flex max-w-prose items-start gap-2 text-dash-xs text-muted">
          <InfoIcon size={14} />
          <span>{t.motionOsNote}</span>
        </p>
      </div>

      <div
        className="mt-6 max-w-xl rounded-xl bg-surface p-4"
        style={cardStyle}
      >
        <h2 className="text-dash-md font-bold">{t.playModeTitle}</h2>
        {/* 🔴 A CHOICE, NOT A FALLBACK — `playPreference.ts`'s whole argument.
            And it changes nothing about difficulty: the assessment is identical
            down every path, which is what makes offering it honest. */}
        <p className="mt-1 max-w-prose text-muted">{t.playModeWhy}</p>

        <div className="mt-4 flex flex-wrap gap-3">
          {PLAY_MODES.map((m) => (
            <DashButton
              key={m}
              variant={m === mode ? "primary" : "secondary"}
              onClick={() => {
                setMode(m);
                setPlayMode(m);
              }}
            >
              {t.playMode[m]}
            </DashButton>
          ))}
        </div>

        <p className="mt-4 max-w-prose text-dash-xs text-muted">
          {t.playModeSameReward}
        </p>
      </div>
    </SettingsFrame>
  );
}

/* ─────────────────────────────────────────────────────────── G4 ── */

/**
 * G4 — camera and privacy.
 *
 * 🔴 THIS SCREEN'S REAL JOB IS TO STATE INVARIANT I1 IN WORDS A PARENT OR A
 * PRINCIPAL CAN CHECK. Nothing face- or body-derived ever leaves the device: no
 * landmark, no blendshape, no baseline, no frame — not for debugging, not behind
 * a flag. COPPA's amended Rule classifies face templates as personal
 * information, and that single fact is what keeps a camera pointed at children
 * legally simple. A privacy screen that only showed a permission toggle would
 * omit the part that matters.
 *
 * ⚠️ IT OFFERS NO "TURN THE CAMERA OFF" SWITCH, AND THAT IS I3/I4 RATHER THAN AN
 * OMISSION. Every mechanic already has a tap twin on one code path, and an
 * unusable camera renders nothing and routes silently into tap mode — so the
 * off switch is the browser's own permission, and duplicating it here would be a
 * second control that could disagree with the real one.
 */
export function CameraSettingsScreen({
  lang = "en",
  role,
  navigate,
}: SettingsProps) {
  const t = SETTINGS_COPY[lang];
  const [state, setState] = useState<CameraState>("prompt");

  useEffect(() => {
    let live = true;
    const hasDevices =
      typeof navigator !== "undefined" && "mediaDevices" in navigator;

    /*
     * ⚠️ `Permissions.query` IS NOT UNIVERSAL — Firefox has never supported
     * `{ name: 'camera' }`. A rejected query means "cannot tell", never
     * "denied": telling a teacher on Firefox their camera is blocked sends them
     * to a browser setting that will not help.
     */
    const resolve = async () => {
      if (!hasDevices || typeof navigator.permissions?.query !== "function") {
        if (live) setState(describeCameraState(null, hasDevices));
        return;
      }
      try {
        const status = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (live) setState(describeCameraState(status.state, hasDevices));
      } catch {
        if (live) setState(describeCameraState(null, hasDevices));
      }
    };

    void resolve();
    return () => {
      live = false;
    };
  }, []);

  const tone =
    state === "granted"
      ? "success"
      : state === "denied"
        ? "warning"
        : "neutral";

  return (
    <SettingsFrame
      lang={lang}
      active="settings-camera"
      role={role}
      navigate={navigate}
    >
      <div className="max-w-xl rounded-xl bg-surface p-4" style={cardStyle}>
        <h2 className="text-dash-md font-bold">{t.cameraTitle}</h2>

        <p className="mt-3">
          {/* Icon + colour + text, always all three. */}
          <StatusPill
            tone={tone}
            icon={
              state === "granted" ? (
                <CameraIcon size={14} />
              ) : (
                <CameraOffIcon size={14} />
              )
            }
          >
            {t.cameraState[state]}
          </StatusPill>
        </p>

        {/* 🔴 I4 — an unusable camera is never an error for the child. Said here
            because this is adult chrome, where it CAN be explained. */}
        <p className="mt-3 max-w-prose text-muted">{t.cameraFallback}</p>
      </div>

      <div
        className="mt-6 max-w-xl rounded-xl bg-surface p-4"
        style={cardStyle}
      >
        <h2 className="flex items-center gap-2 text-dash-md font-bold">
          <PrivacyIcon size={18} />
          {t.privacyTitle}
        </h2>

        {/* 🔴 INVARIANT I1, IN CHECKABLE SENTENCES. */}
        <ul className="mt-3 flex max-w-prose flex-col gap-2 text-muted">
          {t.privacyPoints.map((point, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckIcon size={14} />
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <p className="mt-4 max-w-prose text-dash-xs text-muted">
          {t.privacyRecording}
        </p>
      </div>

      {/* 🔴 CHANGE 19 §7 — *"recording is OFF by default"*, AND THE SWITCH THAT
          MAKES "IF ENABLED" MEAN ANYTHING. `School.recordingDefault` has existed
          since OD-36 was closed and had no screen anywhere in the product: the
          column, the DTO and the API client were all written, and no admin could
          reach any of them. A default of "off" with no way on is not a setting,
          it is a removal. */}
      {role === "ADMIN" ? <SchoolRecordingPanel lang={lang} /> : null}
    </SettingsFrame>
  );
}

/**
 * Layer 3 of the recording chain, as a switch.
 *
 * 🔴 IT IS RESTRICT-ONLY AND THE COPY HAS TO SAY BOTH HALVES. Turning it on does
 * NOT start recording anybody: layer 1 is `ParentalConsent`, and a child whose
 * parent has not consented is never recorded whatever this says
 * (settings-and-controls §2.2). Turning it off stops everything school-wide,
 * immediately, for every class. An admin who reads this as a master switch is
 * wrong in the safe direction on one side and the dangerous direction on the
 * other, so the sentence beside it does the work.
 *
 * ⚠️ IT TAKES EFFECT FROM THE NEXT SITTING, NOT THIS ONE. `resolveRecording` is
 * evaluated once at session start and stamped — *"a parent revoking consent at
 * 10:31 must not produce half a recorded session with an ambiguous boundary"* —
 * and the same is true of an admin flipping this at 10:31.
 */
function SchoolRecordingPanel({ lang }: { lang: Lang }) {
  const t = SETTINGS_COPY[lang];
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [state, setState] = useState<SurfaceState>("loading");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  /* ⚠️ `getStaffSession()` RETURNS THE WHOLE SESSION, NOT A `StaffAuth`. Only the
     bearer token belongs on a request; spreading the session would put the role
     and the school id into a query object that has no use for either. */
  const token = getStaffSession()?.accessToken;

  useEffect(() => {
    const controller = new AbortController();
    getSchoolSettings({ token, signal: controller.signal })
      .then((row) => {
        setEnabled(row.recordingDefault);
        setState("ready");
      })
      .catch(() => {
        setState("error");
      });
    return () => {
      controller.abort();
    };
  }, [token]);

  const save = (next: boolean) => {
    /*
     * ⚠️ OPTIMISTIC, AND REVERTED ON FAILURE. A toggle that waits for the
     * network before moving reads as broken on a school connection; a toggle
     * that moves and stays moved after a failure is a lie about what is stored,
     * on the one setting in this product that governs video of children.
     */
    const was = enabled;
    setEnabled(next);
    setBusy(true);
    setFailed(false);
    saveSchoolSettings({ recordingDefault: next }, { token })
      .then((row) => {
        setEnabled(row.recordingDefault);
      })
      .catch(() => {
        setEnabled(was);
        setFailed(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="mt-6 max-w-xl rounded-xl bg-surface p-4" style={cardStyle}>
      <h2 className="flex items-center gap-2 text-dash-md font-bold">
        <CameraIcon size={18} />
        {t.recordingTitle}
      </h2>

      <DataSurface state={state} skeletonRows={2} errorMessage={t.recordingLoadFailed}>
        {enabled === null ? null : (
          <div className="mt-3">
            <CheckboxRow
              checked={enabled}
              disabled={busy}
              onChange={save}
              hint={t.recordingHint}
            >
              {t.recordingToggle}
            </CheckboxRow>

            {/* 🔴 THE HALF PEOPLE GET WRONG, SAID SEPARATELY FROM THE HINT.
                Consent is the floor; this switch can only ever restrict. */}
            <p className="mt-3 max-w-prose text-dash-xs text-muted">
              {t.recordingConsentFloor}
            </p>

            {failed ? (
              <p className="mt-3 max-w-prose text-dash-xs font-bold text-primary">
                {t.recordingSaveFailed}
              </p>
            ) : null}
          </div>
        )}
      </DataSurface>
    </div>
  );
}

